import React, { useState } from "react";
import {
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Popper,
  Paper,
  ClickAwayListener,
  Fade,
  TableSortLabel,
  Button,
  Box,
  Alert,
  Chip,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import PreviewIcon from "@mui/icons-material/Preview";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { v4 as uuidv4 } from "uuid";
import { useSnackbar } from "notistack";

import customTheme from "../../theme/customTheme";
import Styled from "../../theme/CustomMenu/customMenu";
import StyledTable from "../../theme/CustomTable/CustomTable";
import ConfirmationDialog from "../../components/Dialog/ConfirmationDialog";
import { useAppSelector } from "../../hooks/storeHooks";
import { setTableSeting } from "../../redux/settingSlice";
import AddEditReaderApp from "./AddEditReaderTypes/AddEditReaderTypes";
import Search from "../../components/Search";
import {
  useDeleteReaderTypeMutation,
  useGetReaderTypesQuery,
} from "../../redux/api/readerTypeApis";
import usePagination from "../../hooks/usePagination";

enum SortingDirectionEnum {
  ASCENDING = "asc",
  DESCENDING = "desc",
}

type SortingDirection = `${SortingDirectionEnum}`;

type HeadCell = {
  id: string;
  label: string;
  isSorting: boolean;
};

const headCells: readonly HeadCell[] = [
  {
    id: "name",
    label: "Reader name",
    isSorting: true,
  },

  {
    id: "antenna",
    label: "Antenna",
    isSorting: true,
  },
  {
    id: "status",
    label: "Status",
    isSorting: false,
  },
];

export default function ReaderTypes() {
  // for pagination
  const {
    rowsPerPage,
    page,
    setPage,
    handleChangeRowsPerPage,
    handleChangePage,
  } = usePagination();
  const { dense } = useAppSelector(setTableSeting);
  const [name, setName] = React.useState("");
  const [isEdit, setIsEdit] = React.useState(false);
  const [preview, setPreview] = React.useState(false);
  const [deleteReaderType] = useDeleteReaderTypeMutation();
  const { data: readerTypes } = useGetReaderTypesQuery();
  const [order, setOrder] = useState<SortingDirection>(
    SortingDirectionEnum.DESCENDING
  );
  const [orderBy, setOrderBy] = useState("name");
  const [showDialog, setShowDialog] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [rowId, setRowId] = useState("");
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | any>(null);
  const { enqueueSnackbar } = useSnackbar();

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order: SortingDirection, orderBy: string) {
    return order === SortingDirectionEnum.DESCENDING
      ? (a: Record<string, unknown>, b: Record<string, unknown>) =>
        descendingComparator(a, b, orderBy)
      : (a: Record<string, unknown>, b: Record<string, unknown>) =>
        -descendingComparator(a, b, orderBy);
  }

  const handleCloseDialog = () => {
    setShowDialog(false);
    setRowId("");
    setOpen(false);
    setIsEdit(false);
    setPreview(false);
  };

  const handleDeleteDialog = () => {
    setConfirmationDialog(false);
  };

  const handleDeleteReaderApp = () => {
    deleteReaderType(rowId)
      .unwrap()
      .then(() =>
        enqueueSnackbar("Success", {
          variant: "success",
          autoHideDuration: 2000,
        })
      )
      .catch(() =>
        enqueueSnackbar("Failed", {
          variant: "error",
          autoHideDuration: 2000,
        })
      );
    setConfirmationDialog(false);
    setRowId("");
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      handleRequestSort(event, property);
    };

  const handleClose = () => {
    setOpen(false);
  };

  const handleContextMenu = (e: React.MouseEvent, rowId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const { clientX, clientY } = e;
    setOpen(true);
    setRowId(rowId);
    const virtualElement = {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: clientY,
        right: clientX,
        bottom: clientY,
        left: clientX,
      }),
    };
    setAnchorEl(virtualElement);
  };

  const handleToggleEdit = () => {
    setIsEdit(true);
    setShowDialog(true);
  };
  const id = open ? "reader-app-managment-menu" : undefined;

  // get background color of table row
  function getTableRowColor(
    rowId: string | number,
    selectedRowId: string | number | null
  ) {
    const highlightColor = customTheme.palette.secondary.main;
    const defaultColor = customTheme.palette.secondary.light;
    if (selectedRowId) {
      return selectedRowId === rowId ? highlightColor : defaultColor;
    }
    return defaultColor;
  }
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    setPage(1);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="contained"
          onClick={() => {
            setShowDialog(true);
            setRowId("");
          }}
        >
          <AddCircleIcon sx={{ marginRight: "0.5rem" }} /> Create Reader Type
        </Button>
        <Search name={name} handleSearch={handleSearch} />
      </Box>
      <StyledTable>
        <TableContainer>
          <Table
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
            sx={dense ? { marginTop: "1rem" } : null}
          >
            <TableHead>
              <TableRow>
                {headCells.map((item) => (
                  <TableCell key={item.id}>
                    {item.isSorting ? (
                      <TableSortLabel
                        active={orderBy === `${item.id}`}
                        direction={orderBy === `${item.id}` ? order : "asc"}
                        onClick={createSortHandler(item.id)}
                      >
                        {item.label}
                      </TableSortLabel>
                    ) : (
                      item.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {readerTypes &&
                readerTypes?.results
                  ?.slice(rowsPerPage * 0, rowsPerPage * (page + 1))
                  ?.sort(getComparator(order, orderBy))
                  ?.map((data) => (
                    <TableRow
                      key={uuidv4()}
                      onContextMenu={(event) =>
                        handleContextMenu(event, data?.id)
                      }
                      style={{
                        backgroundColor: getTableRowColor(data?.id, rowId),
                      }}
                    >
                      <TableCell>{data?.name}</TableCell>
                      <TableCell>{data?.ui_configuration?.antenna}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            data?.disabled === true ? "Disabled" : "Active"
                          }
                          color={data?.disabled === true ? "error" : "success"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledTable>
      <TablePagination
        rowsPerPageOptions={[20, 25, 50, 100]}
        component="div"
        count={readerTypes?.count || 0}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {anchorEl && (
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          transition
          placement="bottom-start"
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleClose}>
              <Fade {...TransitionProps}>
                <Paper>
                  <Styled>
                    <MenuItem onClick={() => handleToggleEdit()}>
                      <ModeEditIcon />
                      Edit
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setShowDialog(true);
                        setPreview(true);
                      }}
                    >
                      <PreviewIcon />
                      Preview
                    </MenuItem>
                    <MenuItem onClick={() => setConfirmationDialog(true)}>
                      <DeleteIcon />
                      Delete
                    </MenuItem>
                  </Styled>
                </Paper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      )}

      {showDialog && (
        <AddEditReaderApp
          isOpen={showDialog}
          handleCloseDialog={handleCloseDialog}
          id={rowId}
          isEdit={isEdit}
          preview={preview}
        />
      )}
      {confirmationDialog && rowId && (
        <ConfirmationDialog
          isOpen={confirmationDialog}
          onClose={handleDeleteDialog}
          onConfirm={() => handleDeleteReaderApp()}
          title="Delete Reader Type"
          confirmText="Confirm"
          isWarning
        >
          Are you sure you want to delete Reader Type?
        </ConfirmationDialog>
      )}
      <Alert severity="info">
        This part of the application is for only people dealing with Reader
        Applications. Creating a reader type with same name is not possible and
        will throw error. Deleting reader type only disables it.
      </Alert>
    </Box>
  );
}
